import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolhaDeBaixo } from '../FolhaDeBaixo';
import { numero } from '../formato';
import { Icone } from '../Icone';
import { Dialogo, useEstreito } from '../ui';
import { PALAVRA_DA_GRAVIDADE, type Pendencia } from './tipos';

/**
 * Um cartão da pilha — todos do mesmo jeito (RN-256, 3).
 *
 * O número (fora o cartão de um registro só, o do Hub); o que é, em até 8 palavras; a consequência numa linha; um
 * detalhe; a gravidade em palavra E em cor (a cor nunca é a única pista);
 * a área; a ação; e o «i».
 *
 * ── A ação: um botão no PC, o cartão inteiro no celular ───────────────
 *
 * No PC o cartão é para ler, e a ação é um botão no pé dele — o secundário
 * da casa (`botao secundario`, P-UI-10): numa pilha de dez cartões, dez
 * botões cheios seriam dez ações principais. No celular o cartão inteiro
 * é o alvo: um toque e se está na tela que resolve, sem mirar num botão
 * dentro de um cartão estreito. O «i» fica FORA do link (um botão dentro
 * de um link é marcação inválida, e o toque nele abriria a outra tela),
 * preso no canto, por cima.
 *
 * ── O «i» ──────────────────────────────────────────────────────────────
 *
 * Os parágrafos que explicavam cada bloco no meio da tela saíram de lá
 * (decisão de 08/10: «nenhum parágrafo de explicação no meio da tela») e
 * vieram para cá, a um toque. No PC abrem na janela de sempre (`Dialogo`);
 * no celular, na folha que sobe. Sem explicação, não há «i» — um botão que
 * abre uma janela vazia é pior que nenhum. A janela só é montada aberta,
 * para a explicação de trinta cartões não ficar escondida dentro da página.
 */
export function CartaoDePendencia({ p, novo }: { p: Pendencia; novo?: number }) {
  const celular = useEstreito();
  const [aberto, setAberto] = useState(false);
  const info = useRef<HTMLButtonElement>(null);
  const jaAbriu = useRef(false);

  /* A janela é desmontada ao fechar; o foco volta para o «i» que a abriu
     (a folha já faz isso sozinha; a janela, desmontada, não). */
  useEffect(() => {
    if (aberto) { jaAbriu.current = true; return; }
    if (jaAbriu.current) info.current?.focus({ preventScroll: true });
  }, [aberto]);

  const temExplicacao = p.explicacao !== undefined && p.explicacao !== null
    && p.explicacao !== false && p.explicacao !== '';
  const subiu = typeof novo === 'number' && novo > 0 ? novo : 0;

  const cabeca = (
    <>
      {p.numero !== null && <span className="pn-num">{numero(p.numero, 0)}</span>}
      <div className="pn-corpo">
        <h3 className="pn-titulo">{p.titulo}</h3>
        {p.consequencia && <p className="pn-consequencia">{p.consequencia}</p>}
        {p.detalhe && <p className="pn-detalhe">{p.detalhe}</p>}
      </div>
    </>
  );
  const marcas = (
    <p className="pn-marcas">
      <span className="pn-tag">{p.palavra ?? PALAVRA_DA_GRAVIDADE[p.gravidade] ?? p.gravidade} · {p.area}</span>
      {subiu > 0 && <span className="pn-novo">+{subiu} desde a sua última visita</span>}
    </p>
  );

  return (
    <article
      className="pn-cartao"
      data-gravidade={p.gravidade}
      data-chave={p.chave}
      data-area={p.area}
      data-grupo={p.grupo}
      data-info={temExplicacao ? '' : undefined}
      data-sem-numero={p.numero === null ? '' : undefined}
    >
      {celular ? (
        <Link to={p.acao.para} className="pn-cartao-link">
          {cabeca}
          <div className="pn-pe">
            {marcas}
            <span className="pn-seta"><Icone nome="direita" /></span>
          </div>
          <span className="só-leitor">{` · ${p.acao.texto}`}</span>
        </Link>
      ) : (
        <>
          {cabeca}
          <div className="pn-pe">
            {marcas}
            <Link to={p.acao.para} className="botao secundario pn-acao">
              {p.acao.texto}
              <Icone nome="direita" />
            </Link>
          </div>
        </>
      )}

      {temExplicacao && (
        <button
          ref={info}
          type="button"
          className="pn-info"
          aria-label="Por que isto importa"
          title="Por que isto importa"
          aria-haspopup="dialog"
          onClick={() => setAberto(true)}
        >
          <span aria-hidden="true">i</span>
        </button>
      )}

      {temExplicacao && aberto && (celular ? (
        <FolhaDeBaixo aberta titulo={p.titulo} aoFechar={() => setAberto(false)}>
          <div className="pn-explicacao">{p.explicacao}</div>
        </FolhaDeBaixo>
      ) : (
        <Dialogo aberto titulo={p.titulo} rotulo="Por que isto importa" aoFechar={() => setAberto(false)}>
          <div className="pn-explicacao">{p.explicacao}</div>
        </Dialogo>
      ))}
    </article>
  );
}
